import {configureStore} from '@reduxjs/toolkit';
import {setupListeners} from '@reduxjs/toolkit/query';
import {persistStore, persistReducer, createMigrate} from 'redux-persist';
import type {MigrationManifest, PersistedState} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import toast from './reducers/toast';
import auth from './reducers/auth';
import filter from './reducers/filter';
import api from './reduxQuery/api';
import {rtkQueryErrorLogger} from './reduxQuery/middleware/errorHandler';
import {crossTabSyncMiddleware} from './reduxQuery/middleware/crossTabMiddleware';
import {setupCrossTabSync} from './reduxQuery/crossTabSync';

// Table filters survive reloads, so a filter still pinned to the old
// `hold_requests_from_ciox` status would silently match zero rows after the
// rename to `provider_outreach`. Rewrite the saved value in place rather than
// throwing away everyone's filters. The shape is loose (nested value/label
// pairs per page), so walk it rather than reaching for a fixed path.
const LEGACY_STATUS_RENAMES: Record<string, string> = {
  hold_requests_from_ciox: 'provider_outreach',
  'Hold Requests from Ciox': 'Provider Outreach',
};

const renameLegacyStatuses = (value: any): any => {
  if (typeof value === 'string') {
    return LEGACY_STATUS_RENAMES[value] ?? value;
  }
  if (Array.isArray(value)) {
    return value.map(renameLegacyStatuses);
  }
  if (value && typeof value === 'object') {
    const next: Record<string, any> = {};
    Object.keys(value).forEach((key) => {
      next[key] = renameLegacyStatuses(value[key]);
    });
    return next;
  }
  return value;
};

const filterMigrations: MigrationManifest = {
  1: (state) => renameLegacyStatuses(state) as PersistedState,
};

// Configure persist for filter reducer only
const filterPersistConfig = {
  key: 'filter',
  storage,
  version: 1,
  migrate: createMigrate(filterMigrations),
};

// Create persisted filter reducer
const persistedFilterReducer = persistReducer(filterPersistConfig, filter);

export const store = configureStore({
  reducer: {
    api: api.reducer,
    toast,
    auth,
    filter: persistedFilterReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }).concat(api.middleware, rtkQueryErrorLogger, crossTabSyncMiddleware),
});

// Create persistor
export const persistor = persistStore(store);

// Initialize cross-tab cache sync (broadcasts cache invalidations between tabs)
setupCrossTabSync(store.dispatch);

// Enable refetchOnReconnect behavior (refetchOnFocus is disabled in api.ts)
setupListeners(store.dispatch);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
